// Input types based on your data structure
interface InputLeg {
    fix_identifier: string | null;
    fix_latitude: number | null;
    fix_longitude: number | null;
    fix_altitude1: number | null;
    fix_altitude2: number | null;
    fix_magnetic_variation_west: number | null;
    path_and_termination: string;
    transition: string;
    route_type: string;
    fix_type: string | null;
    sequence_number: number;
    altitude_description: string | null;
}

interface InputData {
    legs: InputLeg[];
}

// Output types
interface Point {
    ident: string;
    lat: number;
    lon: number;
    alt: number;
    magnetic_variation_west: number;
}

interface OutputLeg {
    type: string;
    start: Point;
    end: Point;
}

function convertApproachLegs(inputData: InputData): OutputLeg[] {
    const legs = inputData.legs;
    const outputLegs: OutputLeg[] = [];

    // Filter out legs that don't have valid fix coordinates or are holding patterns
    const validLegs = legs.filter(
        leg =>
            leg.fix_latitude !== null &&
            leg.fix_longitude !== null &&
            leg.fix_identifier !== null &&
            leg.path_and_termination !== 'HM' // Skip holding patterns
    );

    // Sort by sequence number to ensure proper order
    validLegs.sort((a, b) => a.sequence_number - b.sequence_number);

    // Group legs by transition to handle different approach transitions
    const transitionGroups = new Map<string, InputLeg[]>();

    validLegs.forEach(leg => {
        const key = leg.transition || 'main';
        if (!transitionGroups.has(key)) {
            transitionGroups.set(key, []);
        }
        transitionGroups.get(key)!.push(leg);
    });

    // Process each transition group
    transitionGroups.forEach((groupLegs, transition) => {
        // For approach transitions, we need to connect them to the main approach
        if (transition !== 'main' && transition !== '') {
            // Process transition legs
            for (let i = 0; i < groupLegs.length - 1; i++) {
                const currentLeg = groupLegs[i];
                const nextLeg = groupLegs[i + 1];

                const outputLeg = createOutputLeg(currentLeg, nextLeg);
                if (outputLeg) {
                    outputLegs.push(outputLeg);
                }
            }

            // Connect last transition leg to first main approach leg
            const lastTransitionLeg = groupLegs[groupLegs.length - 1];
            const mainLegs = transitionGroups.get('main') || [];
            if (mainLegs.length > 0) {
                const firstMainLeg = mainLegs[0];
                const outputLeg = createOutputLeg(
                    lastTransitionLeg,
                    firstMainLeg
                );
                if (outputLeg) {
                    outputLegs.push(outputLeg);
                }
            }
        }
    });

    // Process main approach legs
    const mainLegs = transitionGroups.get('main') || [];
    for (let i = 0; i < mainLegs.length - 1; i++) {
        const currentLeg = mainLegs[i];
        const nextLeg = mainLegs[i + 1];

        const outputLeg = createOutputLeg(currentLeg, nextLeg);
        if (outputLeg) {
            outputLegs.push(outputLeg);
        }
    }

    return outputLegs;
}

function createOutputLeg(
    startLeg: InputLeg,
    endLeg: InputLeg
): OutputLeg | null {
    // Validate that both legs have required data
    if (
        !startLeg.fix_identifier ||
        !endLeg.fix_identifier ||
        startLeg.fix_latitude === null ||
        startLeg.fix_longitude === null ||
        endLeg.fix_latitude === null ||
        endLeg.fix_longitude === null
    ) {
        return null;
    }

    // Determine leg type based on path_and_termination
    const legType = mapPathTerminationToType(endLeg.path_and_termination);

    // Get altitude for start point
    const startAlt = getAltitude(startLeg);
    const endAlt = getAltitude(endLeg);

    return {
        type: legType,
        start: {
            ident: startLeg.fix_identifier,
            lat: startLeg.fix_latitude,
            lon: startLeg.fix_longitude,
            alt: startAlt,
            magnetic_variation_west: startLeg.fix_magnetic_variation_west || 0,
        },
        end: {
            ident: endLeg.fix_identifier,
            lat: endLeg.fix_latitude,
            lon: endLeg.fix_longitude,
            alt: endAlt,
            magnetic_variation_west: endLeg.fix_magnetic_variation_west || 0,
        },
    };
}

function mapPathTerminationToType(pathTermination: string): string {
    // Map ARINC 424 path termination codes to simple types
    switch (pathTermination) {
        case 'TF': // Track to Fix
        case 'CF': // Course to Fix
        case 'IF': // Initial Fix
            return 'direct';
        case 'AF': // Arc to Fix
            return 'arc';
        case 'RF': // Radius to Fix
            return 'radius';
        case 'DF': // Direct to Fix
            return 'direct';
        case 'FA': // Fix to Altitude
        case 'CA': // Course to Altitude
            return 'direct';
        case 'VI': // Heading to Intercept
            return 'intercept';
        case 'VM': // Heading to Manual Termination
            return 'heading';
        case 'VR': // Heading to Radial Termination
            return 'radial';
        default:
            return 'direct';
    }
}

function getAltitude(leg: InputLeg): number {
    // Priority: fix_altitude1, then fix_altitude2, then 0
    if (leg.fix_altitude1 !== null) {
        return leg.fix_altitude1;
    }
    if (leg.fix_altitude2 !== null) {
        return leg.fix_altitude2;
    }
    return 0;
}

// Example usage:
// const convertedLegs = convertApproachLegs(yourInputData);

export { convertApproachLegs };
export type { InputData, OutputLeg, Point };
